import { useState, useEffect } from 'react'
import {
    Box, Card, CardContent, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Chip, Avatar, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid,
    LinearProgress, IconButton
} from '@mui/material'
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore'
import { getFirebaseDb } from '../../config/firebase'

import toast from 'react-hot-toast'

export default function RidersPage() {
    const [riders, setRiders] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingRider, setEditingRider] = useState<any>(null)
    const [formData, setFormData] = useState({ name: '', phone: '', cnic: '', vehicleType: 'bike', areas: '' })

    const db = getFirebaseDb()

    useEffect(() => {
        const ridersQuery = query(collection(db, 'deliveryBoys'), orderBy('createdAt', 'desc'))
        const unsubscribe = onSnapshot(ridersQuery, (snapshot) => {
            const riderData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            setRiders(riderData)
            setLoading(false)
        })
        return () => unsubscribe()
    }, [db])

    const handleOpenDialog = (rider?: any) => {
        if (rider) {
            setEditingRider(rider)
            setFormData({
                name: rider.personalInfo?.name || '',
                phone: rider.personalInfo?.phone || '',
                cnic: rider.personalInfo?.cnic || '',
                vehicleType: rider.workInfo?.vehicleType || 'bike',
                areas: rider.workInfo?.assignedAreas?.join(', ') || ''
            })
        } else {
            setEditingRider(null)
            setFormData({ name: '', phone: '', cnic: '', vehicleType: 'bike', areas: '' })
        }
        setDialogOpen(true)
    }

    const handleSubmit = async () => {
        try {
            const riderData = {
                personalInfo: { name: formData.name, phone: formData.phone, cnic: formData.cnic, address: {} },
                workInfo: { vehicleType: formData.vehicleType, assignedAreas: formData.areas.split(',').map(a => a.trim()), employeeId: `RDR-${Date.now()}` },
                status: 'active',
                performance: { totalDeliveries: 0, successfulDeliveries: 0, averageRating: 0, onTimeDeliveryRate: 0 },
                currentAssignments: [],
                updatedAt: Timestamp.now()
            }
            if (editingRider) {
                await updateDoc(doc(db, 'deliveryBoys', editingRider.id), riderData)
                toast.success('Rider updated')
            } else {
                await addDoc(collection(db, 'deliveryBoys'), { ...riderData, createdAt: Timestamp.now() })
                toast.success('Rider added')
            }
            setDialogOpen(false)
        } catch (error) {
            toast.error('Failed to save rider')
        }
    }

    const handleDelete = async (id: string) => {
        if (confirm('Delete this rider?')) {
            try {
                await deleteDoc(doc(db, 'deliveryBoys', id))
                toast.success('Rider deleted')
            } catch { toast.error('Failed to delete') }
        }
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>Riders ({riders.length})</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>Add Rider</Button>
            </Box>

            <Card>
                <CardContent>
                    {loading ? <LinearProgress /> : riders.length === 0 ? (
                        <Typography sx={{ color: 'grey.500', textAlign: 'center', py: 4 }}>No riders found</Typography>
                    ) : (
                        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                            <Table>
                                <TableHead><TableRow><TableCell>Rider</TableCell><TableCell>Phone</TableCell><TableCell>Vehicle</TableCell><TableCell>Areas</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                                <TableBody>
                                    {riders.map((rider) => (
                                        <TableRow key={rider.id} hover>
                                            <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}><Avatar sx={{ bgcolor: 'secondary.main' }}>{rider.personalInfo?.name?.charAt(0)}</Avatar><Typography variant="body2" sx={{ fontWeight: 500 }}>{rider.personalInfo?.name}</Typography></Box></TableCell>
                                            <TableCell>{rider.personalInfo?.phone}</TableCell>
                                            <TableCell><Chip label={rider.workInfo?.vehicleType} size="small" /></TableCell>
                                            <TableCell>{rider.workInfo?.assignedAreas?.join(', ') || 'N/A'}</TableCell>
                                            <TableCell><Chip label={rider.status} color={rider.status === 'active' ? 'success' : 'default'} size="small" /></TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => handleOpenDialog(rider)}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDelete(rider.id)}><DeleteIcon fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingRider ? 'Edit Rider' : 'Add New Rider'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}><TextField fullWidth label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></Grid>
                        <Grid item xs={6}><TextField fullWidth label="Phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></Grid>
                        <Grid item xs={6}><TextField fullWidth label="CNIC" value={formData.cnic} onChange={(e) => setFormData({ ...formData, cnic: e.target.value })} /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Assigned Areas (comma-separated)" value={formData.areas} onChange={(e) => setFormData({ ...formData, areas: e.target.value })} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}><Button onClick={() => setDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleSubmit}>{editingRider ? 'Update' : 'Add'}</Button></DialogActions>
            </Dialog>
        </Box>
    )
}
